/* eslint-disable no-underscore-dangle */
/* eslint-disable no-use-before-define */
import { addFlashMessageForNextPageLoad } from 'utils/cookies';
import HttpError from './HttpError';
import RichError from './RichError';

const handleErrors = (status, url, resolve, reject, options = {}) => {
  if (status > 199 && status < 300) {
    resolve();
    return;
  }

  if (options.skipRedirects) {
    reject();
    return;
  }

  let loginPath = '/login';
  let homePath = `/${USER_LOCALE}/`;

  if (window.location.pathname.startsWith('/fair')) {
    homePath = '/fair';
    loginPath = '/fair/login';
  }

  if (/\/[^/]/.test(url)) {
    // don't redirect on 401/403 for algolia requests
    if (status === 401) {
      addFlashMessageForNextPageLoad('LOGIN_REQUIRED');
      window.location = loginPath;
      return;
    }
    if (status === 403) {
      addFlashMessageForNextPageLoad('FORBIDDEN');
      window.location = homePath;
      return;
    }

    if (
      (status === 408 /* Request Timeout */ || status >= 500) &&
      options.retriesRemaining
    ) {
      setTimeout(() => {
        getJsonInternal(url, resolve, reject, options.retriesRemaining - 1);
      }, 1000);
      return;
    }

    reject();
    return;
  }
  reject();
};

export const initXMLHttpRequestActiveCounter = () => {
  XMLHttpRequest._allRequests = [];
  XMLHttpRequest._completedRequests = [];
  const oldOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function open(...args) {
    this._url = args[1];
    oldOpen.apply(this, args);
  };
  const oldSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function send(...args) {
    if (!this._url.includes('sockjs-node')) {
      XMLHttpRequest._allRequests.push(this._url);
      this.addEventListener('loadend', () => {
        setTimeout(() => {
          XMLHttpRequest._completedRequests.push(this._url);
        }, 1);
      });
    }
    oldSend.apply(this, args);
  };
};

const checkVersion = version => {
  if (
    window &&
    window.Idealist &&
    typeof window.Idealist._version !== 'undefined' &&
    window.Idealist.forceUpdate &&
    typeof version !== 'undefined' &&
    version > window.Idealist._version
  ) {
    window.Idealist.forceUpdate();
  }
};

function parseJsonResponse(xhr, reject) {
  if (!xhr.responseText) {
    return null;
  }
  if (xhr.getResponseHeader('Content-Type').indexOf('json') < 0) {
    reject(
      new RichError('Invalid Content-Type for JSON response', {
        responseURL: xhr.responseURL,
        contentType: xhr.getResponseHeader('Content-Type')
      })
    );
  }
  try {
    const json = JSON.parse(xhr.responseText);
    checkVersion(json._version);
    return json;
  } catch (err) {
    reject(
      new RichError('Error parsing JSON response', {
        responseURL: xhr.responseURL,
        errorMessage: err.message
      })
    );
  }
}

export function getJson(url) {
  return new Promise((resolve, reject) => {
    getJsonInternal(url, resolve, reject, 5 /* retriesRemaining */);
  });
}

const getJsonInternal = (url, resolve, reject, retriesRemaining) => {
  const xhr = new XMLHttpRequest();
  xhr.addEventListener('load', () => {
    handleErrors(
      xhr.status,
      url,
      () => resolve(parseJsonResponse(xhr, reject)),
      () => reject(new HttpError('GET', url, xhr)),
      { retriesRemaining }
    );
  });
  xhr.open('GET', url, true);
  xhr.setRequestHeader('Accept', 'application/json');
  xhr.send();
};

export const postData = (url, formData) => {
  const retriesRemaining = 1;
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.addEventListener('load', () => {
      handleErrors(
        xhr.status,
        url,
        () => resolve(xhr.responseText),
        () => reject(new HttpError('GET', url, xhr)),
        { retriesRemaining }
      );
    });
    xhr.open('POST', url, true);
    xhr.send(formData);
  });
};

export function postJson(url, data, options = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.addEventListener('load', () => {
      handleErrors(
        xhr.status,
        url,
        () => resolve(parseJsonResponse(xhr, reject)),
        () => reject(new HttpError('POST', url, xhr))
      );
    });
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('Content-Type', 'application/json');
    if (options.headers && Object.keys(options.headers).length > 0) {
      Object.entries(options.headers).forEach(([k, v]) => {
        xhr.setRequestHeader(k, v);
      });
    }
    xhr.send(JSON.stringify(data));
  });
}

export function putJson(url, data) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.addEventListener('load', () => {
      handleErrors(
        xhr.status,
        url,
        () => resolve(parseJsonResponse(xhr, reject)),
        () => reject(new HttpError('PUT', url, xhr))
      );
    });
    xhr.open('PUT', url, true);
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.send(JSON.stringify(data));
  });
}

export function patchJson(url, data) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.addEventListener('load', () => {
      handleErrors(
        xhr.status,
        url,
        () => resolve(parseJsonResponse(xhr, reject)),
        () => reject(new HttpError('PUT', url, xhr))
      );
    });
    xhr.open('PATCH', url, true);
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.send(JSON.stringify(data));
  });
}

export function post(url, data, options = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.addEventListener('load', () => {
      handleErrors(
        xhr.status,
        url,
        () => resolve(xhr.responseText),
        () => reject(new HttpError('POST', url, xhr)),
        // don't redirect on 401/403 for algolia requests
        { skipRedirects: !/^\/[^/]/.test(url) }
      );
    });
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Accept', 'application/json');

    if (options.contentType !== false) {
      xhr.setRequestHeader(
        'Content-Type',
        options.contentType || 'application/x-www-form-urlencoded'
      );
    }
    xhr.send(data);
  });
}

export function del(url) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.addEventListener('load', () => {
      handleErrors(
        xhr.status,
        url,
        () => {
          if (xhr.responseText) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            resolve();
          }
        },
        () => reject(new HttpError('DELETE', url, xhr))
      );
    });
    xhr.open('DELETE', url, true);
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.send();
  });
}
